import { fromBase64, toHex, utf8 } from '../bytes/encoding';
import { sha256 } from '../crypto/sha256';
import type { LogDescriptor, LogKind } from '../log/client';
import type { KeyValueStore } from './fetch';
import { withSlash } from './schema';

/** A user-added log, as stored in localStorage. */
export interface CustomLog {
  kind: LogKind;
  description: string;
  /** RFC 6962 base URL, or the static-ct-api submission prefix. */
  url: string;
  /** static-ct-api monitoring prefix (defaults to url). */
  monitoringUrl?: string;
  /** base64 SPKI; optional — without it, signatures show as "no key". */
  key?: string;
}

export const CUSTOM_LOGS_KEY = 'ct:customLogs:v1';

export function loadCustomLogs(store: KeyValueStore): CustomLog[] {
  try {
    const raw = store.getItem(CUSTOM_LOGS_KEY);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? (v as CustomLog[]).filter((c) => c && typeof c.url === 'string') : [];
  } catch {
    return [];
  }
}

export function saveCustomLogs(store: KeyValueStore, logs: CustomLog[]): void {
  store.setItem(CUSTOM_LOGS_KEY, JSON.stringify(logs));
}

export function normalizeLogUrl(u: string): string {
  const t = u.trim();
  if (!/^https?:\/\//i.test(t)) throw new Error('log URL must start with http:// or https://');
  return withSlash(t);
}

/** Builds a descriptor; the id is the hex log ID when a key is given, else a hash of the URL. */
export async function customLogToDescriptor(c: CustomLog): Promise<LogDescriptor> {
  const url = normalizeLogUrl(c.url);
  const monitoringUrl = c.kind === 'tiled' ? normalizeLogUrl(c.monitoringUrl || c.url) : url;
  const d: LogDescriptor = {
    id: '',
    kind: c.kind,
    description: c.description?.trim() || new URL(url).host,
    operator: 'Custom',
    url,
    submissionUrl: url,
    monitoringUrl,
    state: 'unknown',
    programs: [],
    custom: true,
  };
  if (c.key && c.key.trim()) {
    const key = fromBase64(c.key);
    const logId = await sha256(key);
    d.key = key;
    d.logId = logId;
    d.id = toHex(logId);
  } else {
    d.id = 'custom-' + toHex(await sha256(utf8(url + '|' + monitoringUrl))).slice(0, 16);
  }
  return d;
}
